import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './ParentDashboard.css';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ParentDashboard = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardStats, setDashboardStats] = useState({
    totalChildren: 0,
    activeChildren: 0,
    averagePerformance: 0,
    totalTestsTaken: 0,
    totalGamesSessions: 0,
    totalStudyHours: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [childrenResponse, statsResponse] = await Promise.all([
        api.get('/users/children'),
        api.get('/users/parent-stats')
      ]);

      if (childrenResponse.data.success) {
        setChildren(childrenResponse.data.data.children);
      }

      if (statsResponse.data.success) {
        setDashboardStats(statsResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return '#4CAF50'; // Green
    if (score >= 60) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const formatStudyTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="parent-dashboard">
        <div className="loading-container">
          <LoadingSpinner size="large" text="Loading dashboard..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parent-dashboard">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-dashboard">
      <div className="dashboard-header">
        <h1>Parent Dashboard</h1>
        <p>Welcome back, {user?.firstName}! Monitor your children's progress and performance.</p>
      </div>

      {/* Overview Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{dashboardStats.totalChildren}</h3>
            <p>Total Children</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-content">
            <h3>{dashboardStats.activeChildren}</h3>
            <p>Active This Week</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="stat-content">
            <h3>{dashboardStats.averagePerformance}%</h3>
            <p>Average Performance</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-content">
            <h3>{formatStudyTime(dashboardStats.totalStudyHours)}</h3>
            <p>Total Study Time</p>
          </div>
        </div>
      </div>

      {/* Children Overview */}
      <div className="children-section">
        <div className="section-header">
          <h2>Children Overview</h2>
          <button 
            className="manage-btn"
            onClick={() => window.location.href = '/parent/children'}
          >
            Manage Children
          </button>
        </div>

        {children.length === 0 ? (
          <div className="no-children">
            <div className="no-children-icon">
              <i className="fas fa-user-plus"></i>
            </div>
            <h3>No Children Added</h3>
            <p>Add your children to start monitoring their progress</p>
            <button 
              className="add-child-btn"
              onClick={() => window.location.href = '/parent/children'}
            >
              Add Child
            </button>
          </div>
        ) : (
          <div className="children-grid">
            {children.map((child) => (
              <div key={child._id} className="child-card">
                <div className="child-header">
                  <div className="child-avatar">
                    <img 
                      src={child.profilePicture || '/default-avatar.png'} 
                      alt={child.fullName}
                      onError={(e) => {
                        e.target.src = '/default-avatar.png';
                      }}
                    />
                  </div>
                  <div className="child-info">
                    <h3>{child.fullName}</h3>
                    <p className="child-grade">Grade {child.grade}</p>
                    <span className={`status ${child.isActive ? 'active' : 'inactive'}`}>
                      {child.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="child-stats">
                  <div className="stat-row">
                    <span className="stat-label">Recent Performance:</span>
                    <span 
                      className="stat-value"
                      style={{ color: getPerformanceColor(child.recentPerformance || 0) }}
                    >
                      {child.recentPerformance || 0}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Tests Taken:</span>
                    <span className="stat-value">{child.testsTaken || 0}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Study Time:</span>
                    <span className="stat-value">{formatStudyTime(child.studyTime || 0)}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Last Active:</span>
                    <span className="stat-value">
                      {child.lastActive ? new Date(child.lastActive).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                </div>

                <div className="child-actions">
                  <button 
                    className="view-progress-btn"
                    onClick={() => window.location.href = `/parent/child/${child._id}/progress`}
                  >
                    View Progress
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="activity-section">
        <h2>Recent Activity</h2>
        <div className="activity-list">
          {children.map((child) => (
            child.recentActivities?.map((activity, index) => (
              <div key={`${child._id}-${index}`} className="activity-item">
                <div className="activity-icon">
                  <i className={`fas ${activity.type === 'test' ? 'fa-quiz' : activity.type === 'game' ? 'fa-gamepad' : 'fa-video'}`}></i>
                </div>
                <div className="activity-content">
                  <h4>{child.fullName}</h4>
                  <p>{activity.description}</p>
                  <span className="activity-time">{new Date(activity.timestamp).toLocaleString()}</span>
                </div>
                <div className="activity-score">
                  {activity.score && (
                    <span 
                      className="score"
                      style={{ color: getPerformanceColor(activity.score) }}
                    >
                      {activity.score}%
                    </span>
                  )}
                </div>
              </div>
            )) || []
          ))}
          
          {children.every(child => !child.recentActivities?.length) && (
            <div className="no-activity">
              <p>No recent activity to display</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;